import { Injectable } from '@angular/core';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { InventoryCheckboxFilterModel } from 'src/app/models/results/inventory-checkbox-filter';
import { InventoryGroup, InventoryGroupName } from 'src/app/models/results/inventory-group';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { constants } from 'src/environments/constants';
import { ConfigService } from '../config/config.service';
import { SortingService } from './sorting/sorting.service';
import { PriceSort, PriceSortDirection } from 'src/app/models/results/price-sort';

@Injectable({
  providedIn: 'root'
})
export class SharedFilteringService {

  constructor(private confSvc: ConfigService, private sortingService: SortingService) { }

  sortByPriceSortDirection(items: InventoryItemDTO[], priceSort: PriceSort): InventoryItemDTO[] {
    return priceSort.sortDirection === PriceSortDirection.LowToHigh ?
      this.sortByPriceLowToHigh(items) :
      this.sortByPriceHighToLow(items);
  }

  sortByPriceLowToHigh(items: InventoryItemDTO[]): InventoryItemDTO[] {
    this.sortingService.updateCurrentPriceSort(new PriceSort(PriceSortDirection.LowToHigh));
    return items.sort((a, b) => a.listPrice - b.listPrice);
  }

  sortByPriceHighToLow(items: InventoryItemDTO[]): InventoryItemDTO[] {
    this.sortingService.updateCurrentPriceSort(new PriceSort(PriceSortDirection.HighToLow));
    return items.sort((a, b) => b.listPrice - a.listPrice);
  }

  filterByMemory(items: InventoryItemDTO[], group: InventoryGroup): InventoryItemDTO[] {
    return items.filter(item => item.memorySize >= group.minimumRAM);
  }

  filterByCPU(items: InventoryItemDTO[], group: InventoryGroup): InventoryItemDTO[] {
    return items.filter(item => this.inventoryItemContainsAllowedCPU(item, group.allowedCPUs));
  }

  inventoryItemContainsAllowedCPU(item: InventoryItemDTO, allowedCPUs: string[]): boolean {
    let result = false;
    if (item.cpuModel) {
      allowedCPUs.forEach((cpuName) => {
        if (item.cpuModel.toLowerCase().includes(cpuName.toLowerCase())) {
          result = true;
        }
      });
      //return allowedCPUs.some(cpuName => item.cpuModel.includes(cpuName));
      return result;
    }
    else return result;
  }

  filterByBacklitKeyboard(items: InventoryItemDTO[], queryParams: InventoryQueryParams): InventoryItemDTO[] {
    if (queryParams.userType.requiresBacklitKeyboard) {
      return items.filter(item => item.hasBacklitKeyboard);
    }
    return items;
  }

  filterByFingerprintSensor(items: InventoryItemDTO[], queryParams: InventoryQueryParams): InventoryItemDTO[] {
    if (queryParams.userType.requiresFingerprintSensor) {
      return items.filter(item => item.hasFingerprintReader);
    }
    return items;
  }

  filterByPrivacyScreen(items: InventoryItemDTO[], queryParams: InventoryQueryParams): InventoryItemDTO[] {
    if (queryParams.userType.requiresPrivacyScreen) {
      return items.filter(item => item.hasPrivacyScreen);
    }
    return items;
  }

  filterByStylus(items: InventoryItemDTO[], queryParams: InventoryQueryParams): InventoryItemDTO[] {
    if (queryParams.userType.requiresStylus) {
      return items.filter(item => item.hasStylus);
    }
    return items;
  }

  filterByParallels(itemsToFilter: InventoryItemDTO[]): InventoryItemDTO[] {
    return itemsToFilter.filter(item => item.isParallelsCapable);
  }

  filterByFormFactorCheckboxes(itemsToFilter: InventoryItemDTO[], queryParams: InventoryQueryParams, filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.formFactorCheckboxesSelected(filterItemsFromCheckboxParams)) {
      itemsToFilter = this.filterByFormFactor(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
    }

    return itemsToFilter;
  }

  filterByFormFactor(items: InventoryItemDTO[], queryParams: InventoryQueryParams, filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    let formFactorsToFilterBy: string[] = [];

    if (this.formFactorCheckboxesSelected(filterItemsFromCheckboxParams)) {
      formFactorsToFilterBy = filterItemsFromCheckboxParams!.formFactors;
    } else {
      formFactorsToFilterBy = queryParams.userType.allowedFormFactors;
    }

    let descriptionFormFactors: string[] = [];
    let productTypeFormFactors: string[] = [];

    formFactorsToFilterBy.forEach(formFactor => {
      if (formFactor === constants.CHROMEBASE) {
        descriptionFormFactors.push(constants.CHROMEBASE);
      } else if (formFactor === constants.CHROMEBOX) {
        descriptionFormFactors.push(constants.CHROMEBOX);
      } else if (formFactor === constants.CLAMSHELL) {
        productTypeFormFactors.push(...[
          this.confSvc.getString("chromebook"),
          this.confSvc.getString("chromebookDetachableKeyboard"),
          this.confSvc.getString("chromebookNoKeyboard"),
          this.confSvc.getString("chromebookLayFlatDesign"),
          this.confSvc.getString("notebook")
        ]);
      } else if (formFactor === constants.CONVERTIBLE) {
        productTypeFormFactors.push(...[
          this.confSvc.getString("chromebookDetachableKeyboard"),
          this.confSvc.getString("chromebookFlipDesign"),
          this.confSvc.getString("notebookFlipDesign")
        ]);
      } else if (formFactor === constants.TABLET) {
        descriptionFormFactors.push(this.confSvc.getString("tablet"));
      } else if (formFactor === constants.CHROMEBOOKPLUS) {
        productTypeFormFactors.push(...[
          this.confSvc.getString("chromebookPlusFilterLabel")
        ]);
      }
    });

    return items.filter(item => this.itemProductTypeHasOneOfFormFactors(item, productTypeFormFactors) || this.itemDescriptionHasOneOfFormFactors(item, descriptionFormFactors));
  }

  formFactorCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.formFactors && filterItemsFromCheckboxParams.formFactors.length > 0);
  }

  itemProductTypeHasOneOfFormFactors(item: InventoryItemDTO, formFactors: string[]): boolean {
    return formFactors.some(formFactor => item.productType === formFactor);
  }

  itemDescriptionHasOneOfFormFactors(item: InventoryItemDTO, formFactors: string[]): boolean {
    return formFactors.some(formFactor => item.productDescShort.includes(formFactor) || item.productDescLong.includes(formFactor));
  }

  filterByFeatureCheckboxes(items: InventoryItemDTO[], filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.featureCheckboxesSelected(filterItemsFromCheckboxParams)) {

      filterItemsFromCheckboxParams!.features.forEach(feature => {
        if (feature === constants.TOUCHSCREEN) {
          items = items.filter(item => this.itemLongDescriptionHasOneOfPhrases(item, [this.confSvc.getString("touchscreenFeatureText")]) || item.hasTouchscreen);

        }
      });
    }

    return items;
  }

  filterByAvailabilityCheckboxes(items: InventoryItemDTO[], filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.availabilityCheckboxesSelected(filterItemsFromCheckboxParams)) {
      filterItemsFromCheckboxParams!.availability.forEach(availability => {
        if (availability === constants.INSTOCK) {
          items = items.filter(item => 
            {
              return item.isInStock;
            }
            );
        }
      });
    }

    return items;
  }

  filterByChromebookPlusCheckboxes(items: InventoryItemDTO[], filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.chromebookPlusCheckboxesSelected(filterItemsFromCheckboxParams)) {
      filterItemsFromCheckboxParams!.chromebookPlus.forEach(chromebookPlus => {
        if (chromebookPlus === 'Chromebook Plus') {
          items = items.filter((item) => 
            {
              return /Chromebook\s*Plus/i.test(item.productDescShort);
            }
            );
        }
      });
    }

    return items;
  }

  availabilityCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.availability && filterItemsFromCheckboxParams.availability.length > 0);
  }

  chromebookPlusCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.chromebookPlus && filterItemsFromCheckboxParams.chromebookPlus.length > 0);
  }

  featureCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.features && filterItemsFromCheckboxParams.features.length > 0);
  }

  filterByOsTypeCheckboxes(items: InventoryItemDTO[], filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    const isFirstResultsQuery: boolean = filterItemsFromCheckboxParams === undefined;

    if (!isFirstResultsQuery && this.osTypeCheckboxesSelected(filterItemsFromCheckboxParams) && !this.allOsTypeCheckboxesSelected(filterItemsFromCheckboxParams)) {
      filterItemsFromCheckboxParams!.osTypes.forEach(type => {
        if (type === constants.ENTERPRISE) {
          items = items.filter(item => this.isEnterpriseDevice(item));
        } else if (type === constants.NON_ENTERPRISE) {
          items = items.filter(item => !this.isEnterpriseDevice(item));
        }else if (type === constants.CHROME_X) {
          items = items.filter((item) => item.isChromeX);
        }
      });
    }

    return items;
  }

  isEnterpriseDevice(item: InventoryItemDTO): boolean {
    return this.itemShortDescriptionHasOneOfPhrases(item, [constants.CHROMEBOOK_ENTERPRISE, constants.CHROMEBOX_ENTERPRISE, constants.CHROMEBASE_ENTERPRISE]);
  }

  osTypeCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.osTypes && filterItemsFromCheckboxParams.osTypes.length > 0);
  }

  allOsTypeCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return filterItemsFromCheckboxParams!.osTypes.includes(constants.ENTERPRISE) && filterItemsFromCheckboxParams!.osTypes.includes(constants.NON_ENTERPRISE);
  }

  itemShortDescriptionHasOneOfPhrases(item: InventoryItemDTO, phrases: string[]): boolean {
    return phrases.some(phrase => item.productDescShort.toLowerCase().includes(phrase.toLowerCase()));
  }

  itemLongDescriptionHasOneOfPhrases(item: InventoryItemDTO, phrases: string[]): boolean {
    return phrases.some(phrase => item.productDescLong.toLowerCase().includes(phrase.toLowerCase()));
  }

  filterByScreenSizeCheckboxes(itemsToFilter: InventoryItemDTO[], queryParams: InventoryQueryParams, filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.screenSizeCheckboxesSelected(filterItemsFromCheckboxParams)) {
      itemsToFilter = this.filterByScreenSize(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
    }

    return itemsToFilter;
  }

  filterByScreenSize(
    items: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {

    if (this.screenSizeCheckboxesSelected(filterItemsFromCheckboxParams)) {
      const checkboxScreenSizes = filterItemsFromCheckboxParams!.screenSizes;

      const min = Math.min(...checkboxScreenSizes);
      const max = Math.max(...checkboxScreenSizes);

      return items.filter(item => {
        return (item.screenSize >= min) &&
          (item.screenSize <= max);
      });

    } else {
      return items.filter(item => {
        return (item.screenSize >= queryParams.userType.minimumScreenSize) &&
          (item.screenSize <= queryParams.userType.maximumScreenSize);
      });
    }
  }

  screenSizeCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.screenSizes && filterItemsFromCheckboxParams.screenSizes.length > 0);
  }

  filterByStorageCheckboxes(itemsToFilter: InventoryItemDTO[], queryParams: InventoryQueryParams, group: InventoryGroup, filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.storageCheckboxesSelected(filterItemsFromCheckboxParams)) {
      itemsToFilter = this.filterByStorage(itemsToFilter, queryParams, group, false, filterItemsFromCheckboxParams);
    }

    return itemsToFilter;
  }

  filterByStorage(items: InventoryItemDTO[], queryParams: InventoryQueryParams, group: InventoryGroup, useParallelsMinStorage: boolean = false, filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    if (this.storageCheckboxesSelected(filterItemsFromCheckboxParams)) {
      const checkboxStorageSizes = filterItemsFromCheckboxParams!.storageSizes;

      const min = Math.min(...checkboxStorageSizes);
      const max = Math.max(...checkboxStorageSizes);

      return items.filter(item => {
        return (item.diskDriveSize >= min) &&
          (item.diskDriveSize <= max);
      });

    } else if (useParallelsMinStorage) {
      return items.filter(item => item.diskDriveSize >= this.confSvc.getNum("parallelsMinStorage"));
    } else {
      return items.filter(item => item.diskDriveSize >= this.getMinStorageToUse(queryParams, group));
    }
  }

  storageCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.storageSizes && filterItemsFromCheckboxParams.storageSizes.length > 0);
  }

  getMinStorageToUse(queryParams: InventoryQueryParams, group: InventoryGroup): number {
    return queryParams.userType.minimumStorage > group.minimumStorage ? queryParams.userType.minimumStorage : group.minimumStorage;
  }

  filterByBrand(
    items: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {

    const isFirstResultsQuery: boolean = filterItemsFromCheckboxParams === undefined;
    if (isFirstResultsQuery) {
      // filter by the brand(s) selected in the "brands" step of the wizard, if any
      if (queryParams.brands.length > 0) {
        return items.filter((item) => {
          return queryParams.brands.some(brand => brand === item.manufactureName);
        });
      }
    } else if (this.brandCheckboxesSelected(filterItemsFromCheckboxParams)) {
      const checkboxBrands = filterItemsFromCheckboxParams!.brands;

      const convertedBrandNamesToDataSourceNames = checkboxBrands.map(item => {
        return this.confSvc.getRelatedFieldFromConfig(item, "Display", "DataSource");
      });

      return items.filter((item) => {
        return convertedBrandNamesToDataSourceNames.some(brand => brand === item.manufactureName);
      });
    }

    return items;
  }

  filterByClinician(
    items: InventoryItemDTO[],
    queryPrams: InventoryQueryParams
  ): InventoryItemDTO[] {
    const group = new InventoryGroup(
    );
    group.name = InventoryGroupName.AppsAndVirtualization;
    group.minimumRAM = 8;
    group.minimumStorage = 25;
    group.allowedCPUs = [
          this.confSvc.getString("intelCeleron"),
          this.confSvc.getString("amdA6"),
          this.confSvc.getString("amdRyzen"),
          this.confSvc.getString("mediaTekMT8183"),
          this.confSvc.getString("inteli3"),
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7"),
          this.confSvc.getString("intelPentium"),
    ];

    items = this.filterByCPU(items, group);
    items = this.filterByMemory(items, group);
    items = this.filterByStorage(items, queryPrams, group, false);
    return items;
  }
  filterByChromeX(
    items: InventoryItemDTO[],
  ): InventoryItemDTO[] {
    return items.filter((item: InventoryItemDTO) => item.isChromeX);
  }

  brandCheckboxesSelected(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): boolean {
    return !!(filterItemsFromCheckboxParams?.brands && filterItemsFromCheckboxParams.brands.length > 0);
  }
}
